import { clsx } from "clsx";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import type { TemplateItem } from "../../types.ts";

interface ItemComboboxProps {
	items: TemplateItem[];
	value: string;
	onChange: (next: string) => void;
	excludeIds?: Set<string>;
	id?: string;
	disabled?: boolean;
}

function normalize(s: string) {
	return s
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "");
}

export function ItemCombobox({
	items,
	value,
	onChange,
	excludeIds,
	id,
	disabled,
}: ItemComboboxProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");

	const candidates = useMemo(
		() => items.filter((item) => !excludeIds?.has(item.id)),
		[items, excludeIds],
	);

	const filtered = useMemo(() => {
		const q = normalize(query.trim());
		if (!q) return candidates;
		return candidates.filter((item) => normalize(item.name).includes(q));
	}, [candidates, query]);

	const selected = candidates.find((item) => item.id === value);
	const indentByDepth = !query.trim();

	function pick(next: string) {
		onChange(next);
		setOpen(false);
		setQuery("");
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					disabled={disabled}
					className="item-combobox__trigger"
				>
					<span
						className={clsx(
							"item-combobox__trigger-text",
							!selected && "is-empty",
						)}
					>
						{selected ? selected.name : "Nenhum (item raiz)"}
					</span>
					<ChevronsUpDown className="item-combobox__chevron" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="item-combobox__content" align="start">
				<div className="item-combobox__search">
					<div className="item-combobox__search-field">
						<Search className="item-combobox__search-icon" />
						<Input
							autoFocus
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Buscar item..."
							className="item-combobox__search-input"
						/>
					</div>
				</div>
				<div className="item-combobox__list">
					<button
						type="button"
						onClick={() => pick("")}
						className={clsx("item-combobox__option", !value && "is-active")}
					>
						<span className="item-combobox__none-label">
							Nenhum (item raiz)
						</span>
						{!value && <Check />}
					</button>
					{filtered.length === 0 ? (
						<p className="item-combobox__empty">Nenhum item encontrado.</p>
					) : (
						filtered.map((item) => {
							const active = item.id === value;
							return (
								<button
									key={item.id}
									type="button"
									onClick={() => pick(item.id)}
									className={clsx("item-combobox__item", active && "is-active")}
									style={
										indentByDepth
											? { paddingLeft: `${0.5 + item.depth}rem` }
											: undefined
									}
								>
									<span className="item-combobox__item-name">{item.name}</span>
									{active && <Check />}
								</button>
							);
						})
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
